import { useEffect, useMemo, useRef, useState } from "react";
import type { CallGroupSummary, CallSummary } from "@backstory/protocol";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "../../auth/store";
import { allowsFeature, useOrgEntitlements } from "../billing/entitlements";
import { EndUser } from "../../components/EndUser";
import { EmptyMark, ErrorState, Skeleton } from "../../components/States";
import { AutoLoadMore, PageHeader, RowMenu, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { BarChart, LineChart, StatTile } from "../insights/charts";
import { InfoButton, MetricWrap, RangePicker, useRange } from "../insights/components";
import { callKeys, callsApi, type CallsOverview, type CallsQuery } from "./api";
import { CallStateBadge, GroupStatusBadge, MediaErrorBadges, PathBadge, QualityMeter, dur, pctText, rateText } from "./components";
import { CallUpgrade } from "./plan";
import "./calls.css";

type Filters = Pick<CallsQuery, "q" | "relayed" | "failedToConnect" | "networkType" | "minQuality" | "maxQuality" | "browser" | "os" | "device" | "release" | "endUser" | "hasIssue">;
const STRING_KEYS: Array<keyof Filters> = ["q", "networkType", "browser", "os", "device", "release", "endUser"];
const BOOL_KEYS: Array<keyof Filters> = ["relayed", "failedToConnect", "hasIssue"];

export function filtersFromSearch(s: Record<string, unknown>): Filters {
  const f: Filters = {};
  for (const k of STRING_KEYS) if (typeof s[k] === "string" && s[k] !== "") (f as Record<string, unknown>)[k] = s[k];
  for (const k of BOOL_KEYS) if (s[k] === "true" || s[k] === true) (f as Record<string, unknown>)[k] = true;
  const min = Number(s.minQuality);
  if (Number.isFinite(min) && String(s.minQuality ?? "") !== "") f.minQuality = min;
  const max = Number(s.maxQuality);
  if (Number.isFinite(max) && String(s.maxQuality ?? "") !== "") f.maxQuality = max;
  return f;
}

/** Filters set in the panel, counted on its button. The search box is its own thing. */
export function activeFilterCount(f: Filters): number {
  const set = (Object.keys(f) as Array<keyof Filters>).filter((k) => k !== "q" && f[k] !== undefined && f[k] !== "" && f[k] !== false);
  // A quality band is one choice even when it sets both ends of the range.
  const quality = set.filter((k) => k === "minQuality" || k === "maxQuality").length;
  return set.length - quality + (quality > 0 ? 1 : 0);
}

/**
 * Quality is picked as a band, matching the meter and the distribution chart,
 * rather than typed as a number. Each band is a min/max pair on the API.
 */
const QUALITY_OPTIONS: Array<{ id: string; label: string; min?: number; max?: number }> = [
  { id: "", label: "Any" },
  { id: "good", label: "Good (85 and up)", min: 85 },
  { id: "fair", label: "Fair (70–84)", min: 70, max: 84 },
  { id: "poor", label: "Poor (50–69)", min: 50, max: 69 },
  { id: "bad", label: "Bad (under 50)", max: 49 },
  { id: "under70", label: "Poor or bad (under 70)", max: 69 },
  { id: "under85", label: "Below good (under 85)", max: 84 },
];

export function qualityOptionFor(f: Pick<Filters, "minQuality" | "maxQuality">): string {
  const hit = QUALITY_OPTIONS.find((o) => o.min === f.minQuality && o.max === f.maxQuality);
  if (hit) return hit.id;
  return f.minQuality == null && f.maxQuality == null ? "" : "custom";
}

/** Bucket label for the quality distribution bars. */
const bucketLabel = (from: number, to: number) => `${from}–${to}`;

/** Same cuts as the quality meter. A bar takes the status of its middle, so an
 *  80–100 bucket is good rather than fair just because it starts at 80. */
const QUALITY_BANDS = [
  { label: "bad", color: "var(--viz-critical)" },
  { label: "poor", color: "var(--viz-serious)" },
  { label: "fair", color: "var(--viz-warning)" },
  { label: "good", color: "var(--viz-good)" },
] as const;

function qualityBand(from: number, to: number) {
  const mid = (from + to) / 2;
  if (mid >= 85) return QUALITY_BANDS[3];
  if (mid >= 70) return QUALITY_BANDS[2];
  if (mid >= 50) return QUALITY_BANDS[1];
  return QUALITY_BANDS[0];
}

/** Rows shown before "Show more", so a long group keeps the card a steady height. */
const BREAKDOWN_PREVIEW = 8;

/** Plain-language explanations behind the "i" buttons. Same wording as the dashboard. */
const hints = {
  calls: "Everyone who started a call in the selected range. People in the same call are one row on the Calls tab. Filters there narrow this too.",
  connect: "Share of calls that never connected. Good is 1% or less. Poor is above 5%.",
  poorCalls: "Share of calls scoring under 70. Good is 2% or less. Poor is above 5%.",
  relayed: "Share of calls that went through a relay server because a direct connection was blocked. Relayed calls tend to have more delay.",
  setup: "How long a typical call took to connect. The p95 line is how long the slowest calls took, ignoring the worst 5%.",
  quality: "Typical call score, from 0 to 100, based on delay, lost audio, and freezes. Good is 85 or higher. Poor is below 70.",
  trend: "Calls that started each hour, and the typical quality score of those calls. Hours with no calls are left blank.",
  distribution: "How many calls landed in each score range. Green is good, yellow is fair, and red needs attention.",
  failures: "Why calls did not connect or ended early, most common first.",
  breakdown: (title: string) => `Calls grouped by ${title.toLowerCase()}. Quality is the typical score for the group. Pick a name to show only those calls.`,
};

/** Phone handset, for the empty state. */
function EmptyCallsMark() {
  return (
    <EmptyMark>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
      </svg>
    </EmptyMark>
  );
}

/** Row actions, shared by the desktop table and the phone cards. The row itself
 *  opens the call, so the menu holds only what the row does not do. Clicks and
 *  keys inside it stop here rather than also opening the call. */
function CallActions({ call, prefix, onCopied }: { call: CallSummary; prefix: string; onCopied: (ok: boolean) => void }) {
  return (
    <span className="call-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <RowMenu label={`Actions for the call on ${call.pc}`} testId={`${prefix}-menu-${call.id}`}>
        <Link to="/sessions/$id" params={{ id: call.sessionId }} search={{ at: Date.parse(call.startedAt) } as never} role="menuitem" data-testid={`${prefix}-replay-${call.id}`}>
          Open session replay
        </Link>
        <button role="menuitem" onClick={() => void navigator.clipboard?.writeText(call.sessionId).then(() => onCopied(true), () => onCopied(false))}>
          Copy session ID
        </button>
      </RowMenu>
    </span>
  );
}

/** Freeze count and total frozen time, or an em dash when the call never froze. */
function freezeText(call: CallSummary): string {
  return call.freezeCount ? `${call.freezeCount} · ${dur(call.freezeMsTotal)}` : "—";
}

function participantText(n: number): string {
  return `${n} participant${n === 1 ? "" : "s"}`;
}

function GroupTableRow({ group, onOpen }: { group: CallGroupSummary; onOpen: () => void }) {
  return (
    <tr
      data-testid={`group-row-${group.id}`}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" ? onOpen() : undefined)}
      tabIndex={0}
      aria-label={`Open ${group.callGroupId}, ${formatRelative(group.startedAt)}`}
    >
      <td>
        <span className="calls-when">{formatRelative(group.startedAt)}</span>
        <div className="muted small">{group.callGroupId}</div>
      </td>
      <td>
        <QualityMeter score={group.qualityScore} />
      </td>
      <td className="calls-col-detail num">—</td>
      <td className="num">{dur(group.durationMs)}</td>
      <td className="num">—</td>
      <td className="calls-col-detail num">—</td>
      <td className="calls-state">
        <span className="cell-badges">
          <GroupStatusBadge status={group.status} />
          {group.issueCount > 0 && <span className="badge warn">{group.issueCount} issue{group.issueCount === 1 ? "" : "s"}</span>}
        </span>
      </td>
      <td className="calls-user small">
        {group.worstParticipant ? (
          <>
            <div>{group.worstParticipant}</div>
            <div className="muted small">{participantText(group.participantCount)}</div>
          </>
        ) : (
          participantText(group.participantCount)
        )}
      </td>
      <td className="calls-row-actions" />
    </tr>
  );
}

function GroupPhoneCard({ group, onOpen }: { group: CallGroupSummary; onOpen: () => void }) {
  return (
    <li
      className="call-card"
      data-testid={`group-card-${group.id}`}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" ? onOpen() : undefined)}
      tabIndex={0}
      role="link"
      aria-label={`Open ${group.callGroupId}, ${formatRelative(group.startedAt)}`}
    >
      <div className="call-card-head">
        <span className="call-card-when">
          <strong>{group.callGroupId}</strong>
          <small className="muted">{formatRelative(group.startedAt)} · {dur(group.durationMs)}</small>
        </span>
        <GroupStatusBadge status={group.status} />
      </div>
      <QualityMeter score={group.qualityScore} width={120} />
      <dl className="call-card-stats">
        <div><dt>Participants</dt><dd>{group.participantCount}</dd></div>
        <div><dt>Weakest</dt><dd>{group.worstParticipant || "—"}</dd></div>
        <div><dt>Signals</dt><dd>{group.issueCount || "—"}</dd></div>
      </dl>
    </li>
  );
}

export function CallsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const ent = useOrgEntitlements(auth.currentOrgId);
  const toast = useToast();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const filters = useMemo(() => filtersFromSearch(search), [search]);
  const tab = search.tab === "calls" ? "calls" : "overview";
  const { range } = useRange();
  const listSearch = useMemo(() => {
    const next = { ...search };
    delete next.view;
    next.tab = "calls";
    return next;
  }, [search]);

  const copied = (ok: boolean) => toast.push(ok ? "success" : "error", ok ? "Session ID copied." : "Could not copy the session ID.");
  /** A row or card opens the conversation. A group opens its participants; a connection without a group opens that call. */
  const openCall = (id: string) => void navigate({ to: "/calls/$callId", params: { callId: id }, search: listSearch as never });
  const openGroup = (id: string) => void navigate({ to: "/calls/groups/$groupId", params: { groupId: id }, search: listSearch as never });

  const setTab = (nextTab: "overview" | "calls") => {
    const next: Record<string, unknown> = { ...search };
    if (nextTab === "calls") next.tab = "calls";
    else delete next.tab;
    delete next.view;
    void navigate({ to: "/calls", search: next as never, replace: true });
  };
  const setFilters = (patch: Partial<Filters> & { tab?: string }) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === "" || next[k] === false) delete next[k];
    delete next.view;
    void navigate({ to: "/calls", search: next as never, replace: true });
  };
  const clearFilters = () => {
    const next: Record<string, unknown> = {};
    for (const k of ["range", "from", "to", "tab"]) if (search[k] != null && search[k] !== "") next[k] = search[k];
    void navigate({ to: "/calls", search: next as never, replace: true });
  };
  const filtered = Object.keys(filters).length > 0;
  const active = activeFilterCount(filters);

  // The search box commits a moment after typing stops, like the sessions list.
  const [qInput, setQInput] = useState(filters.q ?? "");
  useEffect(() => {
    const h = setTimeout(() => {
      if ((filters.q ?? "") !== qInput.trim()) setFilters({ q: qInput.trim() || undefined });
    }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);
  const [showFacets, setShowFacets] = useState(active > 0);
  /** A breakdown row sets a filter and opens Calls, where that choice is in the panel. */
  const pick = (patch: Partial<Filters>) => {
    setFilters({ ...patch, tab: "calls" });
    setShowFacets(true);
  };

  const ov = useQuery({
    queryKey: [...callKeys.overview(range.from, range.to, filters), auth.currentProjectId],
    queryFn: () => callsApi.overview({ ...filters, ...range }),
    // Changing a filter keeps the last numbers on screen until the new ones land.
    placeholderData: keepPreviousData,
  });
  // The dropdowns list every value in the range, not only the ones left after
  // filtering, so a second choice is always reachable.
  const baseOv = useQuery({
    queryKey: [...callKeys.overview(range.from, range.to, {}), auth.currentProjectId],
    queryFn: () => callsApi.overview({ ...range }),
    enabled: filtered,
  });
  const options = (pickList: (o: CallsOverview) => Array<{ key: string }>, current: string | undefined) => {
    const seen = [baseOv.data, ov.data].flatMap((src) => (src ? pickList(src).map((b) => b.key) : []));
    return Array.from(new Set([...seen.filter(Boolean), ...(current ? [current] : [])])).sort();
  };

  const q = useInfiniteQuery({
    queryKey: [...callKeys.feed({ ...filters, ...range }), auth.currentProjectId],
    queryFn: ({ pageParam }) => callsApi.feed({ ...filters, ...range, cursor: pageParam as string | undefined, limit: 50 }),
    enabled: tab === "calls",
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);

  const o = ov.data;
  /** True once the overview has at least one call: rates and medians mean something. */
  const some = o != null && o.calls > 0;
  const [bdOpen, setBdOpen] = useState<Record<string, boolean>>({});
  const distItems = useMemo(
    () =>
      (o?.qualityDistribution ?? []).map((b) => {
        const band = qualityBand(b.from, b.to);
        return {
          label: bucketLabel(b.from, b.to),
          value: b.count,
          color: band.color,
          hint: `${b.count} calls scored ${bucketLabel(b.from, b.to)}, ${band.label}`,
        };
      }),
    [o],
  );

  /** The list head pins under the page head once the list reaches it, and the
   *  column headers pin under the list head in turn. Its height changes when the
   *  Filters panel opens or the toolbar wraps, so it is measured rather than
   *  hard-coded, and published to the card as `--calls-list-head-h`. */
  const listHeadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listHeadRef.current;
    const host = el?.parentElement;
    if (!el || !host) return;
    const sync = () => host.style.setProperty("--calls-list-head-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    sync();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      host.style.removeProperty("--calls-list-head-h");
    };
  }, [tab]);

  /** Search, quick toggles, and the Filters panel. They sit on the list card
   *  itself, right over the rows they narrow, rather than up in the page head
   *  where the charts and breakdowns would push the results off screen. */
  const listHead = (
    <div className="calls-list-head" ref={listHeadRef}>
      <div className="toolbar calls-toolbar">
        <input className="input calls-search" placeholder="Search user, release, network, or call…" value={qInput} onChange={(e) => setQInput(e.target.value)} aria-label="Search calls" autoComplete="off" />
        <button type="button" className="btn" aria-pressed={!!filters.failedToConnect} onClick={() => setFilters({ failedToConnect: !filters.failedToConnect })}>
          Failed to connect
        </button>
        <button type="button" className="btn" aria-pressed={!!filters.hasIssue} onClick={() => setFilters({ hasIssue: !filters.hasIssue })}>
          With issues
        </button>
        <button type="button" className="btn" aria-pressed={!!filters.relayed} onClick={() => setFilters({ relayed: !filters.relayed })}>
          Relayed
        </button>
        <button type="button" className="btn" aria-pressed={showFacets} onClick={() => setShowFacets((v) => !v)} aria-expanded={showFacets} aria-controls="calls-facets">
          Filters {active > 0 && <span className="badge ai">{active}</span>}
        </button>
        <span className="spacer" />
        {filtered && (
          <button type="button" className="btn ghost" onClick={() => { setQInput(""); clearFilters(); }}>
            Clear
          </button>
        )}
      </div>
      {showFacets && (
        <div className="facets" id="calls-facets" aria-label="Filters">
          <label>
            Quality
            <select value={qualityOptionFor(filters)} onChange={(e) => { const opt = QUALITY_OPTIONS.find((x) => x.id === e.target.value); setFilters({ minQuality: opt?.min, maxQuality: opt?.max }); }} autoComplete="off">
              {QUALITY_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              {qualityOptionFor(filters) === "custom" && <option value="custom">Score {filters.minQuality ?? 0}–{filters.maxQuality ?? 100}</option>}
            </select>
          </label>
          <label>
            Network
            <select value={filters.networkType ?? ""} onChange={(e) => setFilters({ networkType: e.target.value || undefined })} autoComplete="off">
              <option value="">Any</option>
              {options((s) => s.byNetworkType, filters.networkType).map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
          <label>
            Browser
            <select value={filters.browser ?? ""} onChange={(e) => setFilters({ browser: e.target.value || undefined })} autoComplete="off">
              <option value="">Any</option>
              {options((s) => s.byBrowser, filters.browser).map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
          <label>
            OS
            <select value={filters.os ?? ""} onChange={(e) => setFilters({ os: e.target.value || undefined })} autoComplete="off">
              <option value="">Any</option>
              {options((s) => s.byOs, filters.os).map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
          <label>
            Device
            <select value={filters.device ?? ""} onChange={(e) => setFilters({ device: e.target.value || undefined })} autoComplete="off">
              <option value="">Any</option>
              {options((s) => s.byDevice, filters.device).map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
          <label>
            Release
            <select value={filters.release ?? ""} onChange={(e) => setFilters({ release: e.target.value || undefined })} autoComplete="off">
              <option value="">All releases</option>
              {options((s) => s.byRelease, filters.release).map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );

  if (ent.isSuccess && !allowsFeature(ent.data, "webrtc_groups")) return <CallUpgrade kind="analytics" />;

  return (
    <>
      <PageHeader title="Calls" actions={<RangePicker />}>
        <nav className="section-tabs" role="tablist" aria-label="Calls">
          <button type="button" role="tab" aria-selected={tab === "overview"} className={tab === "overview" ? "active" : undefined} onClick={() => setTab("overview")}>
            Overview
          </button>
          <button type="button" role="tab" aria-selected={tab === "calls"} className={tab === "calls" ? "active" : undefined} onClick={() => setTab("calls")}>
            Calls
          </button>
        </nav>
      </PageHeader>

      {tab === "calls" ? null : ov.isError ? (
        <ErrorState error={ov.error} retry={() => void ov.refetch()} />
      ) : (
        <section className="calls-overview" aria-label="Call quality overview">
          <MetricWrap about="Calls" hint={hints.calls}>
            <StatTile loading={ov.isPending} label="Calls" value={o?.calls} />
          </MetricWrap>
          <MetricWrap about="Connect failures" hint={hints.connect}>
            <StatTile loading={ov.isPending} label="Connect failures" value={some ? rateText(o.connectFailureRate) : undefined} rating={!some ? undefined : o.connectFailureRate > 0.05 ? "poor" : o.connectFailureRate > 0.01 ? "needs-improvement" : "good"} />
          </MetricWrap>
          <MetricWrap about="Poor quality" hint={hints.poorCalls}>
            <StatTile loading={ov.isPending} label="Poor quality" value={some ? rateText(o.poorQualityRate) : undefined} rating={!some ? undefined : o.poorQualityRate > 0.05 ? "poor" : o.poorQualityRate > 0.02 ? "needs-improvement" : "good"} />
          </MetricWrap>
          <MetricWrap about="Relayed" hint={hints.relayed}>
            <StatTile loading={ov.isPending} label="Relayed" value={some ? rateText(o.relayRate) : undefined} />
          </MetricWrap>
          <MetricWrap about="Setup time" hint={hints.setup}>
            <StatTile loading={ov.isPending} label="Setup time p50" value={some ? o.setupMsP50 : undefined} formatValue={(v) => dur(v)} footer={<span className="muted small">p95 {some && o.setupMsP95 != null ? dur(o.setupMsP95) : "—"}</span>} />
          </MetricWrap>
          <MetricWrap about="Quality" hint={hints.quality}>
            <StatTile loading={ov.isPending} label="Quality p50" value={some ? o.qualityP50 : undefined} rating={!some || o.qualityP50 == null ? undefined : o.qualityP50 >= 85 ? "good" : o.qualityP50 >= 70 ? "needs-improvement" : "poor"} />
          </MetricWrap>
        </section>
      )}

      {tab === "overview" && ov.isPending && (
        <>
          <div className="calls-charts">
            <section className="card calls-card">
              <h2>Calls and median quality</h2>
              <Skeleton lines={5} />
            </section>
            <section className="card calls-card">
              <h2>Quality distribution</h2>
              <Skeleton lines={5} />
            </section>
          </div>
          <section className="card calls-card calls-breakdowns" aria-label="Breakdowns">
            <h2>Breakdowns</h2>
            <Skeleton lines={4} />
          </section>
        </>
      )}

      {tab === "overview" && some && (
        <div className="calls-charts">
          <section className="card calls-card">
            <h2>Calls and median quality <InfoButton about="this chart" hint={hints.trend} /></h2>
            <LineChart
              ariaLabel="Calls per hour and median quality score"
              series={[{ name: "Calls", points: o.series.map((p) => ({ t: p.t, v: p.calls })) }]}
              height={160}
              noTable
            />
            <LineChart
              ariaLabel="Median quality score per hour"
              series={[{ name: "Quality p50", points: o.series.map((p) => ({ t: p.t, v: p.qualityP50 ?? null })), color: "var(--viz-s3)" }]}
              height={140}
              yMax={100}
              noTable
            />
          </section>
          <section className="card calls-card">
            <h2>Quality distribution <InfoButton about="quality distribution" hint={hints.distribution} /></h2>
            <div className="viz">
              <ul className="calls-q-legend" aria-label="Quality colors">
                {QUALITY_BANDS.map((band) => (
                  <li key={band.label}>
                    <i style={{ background: band.color }} aria-hidden="true" />
                    {band.label}
                  </li>
                ))}
              </ul>
              <BarChart items={distItems} ariaLabel="Calls by quality score bucket" orientation="vertical" height={160} />
            </div>
            <h2 style={{ marginTop: 12 }}>Top failure reasons <InfoButton about="failure reasons" hint={hints.failures} /></h2>
            {o.topFailureReasons.length === 0 ? (
              <p className="muted small calls-none">Every call in this range connected and stayed up.</p>
            ) : (
              <BarChart items={o.topFailureReasons.map((r) => ({ label: String(r.reason).replace(/_/g, " "), value: r.count, color: "var(--viz-critical)" }))} ariaLabel="Top call failure reasons" orientation="horizontal" />
            )}
          </section>
        </div>
      )}

      {tab === "overview" && some && (
        <div className="calls-bd-stack" aria-label="Breakdowns">
          {([
            { id: "network", title: "Network", list: o.byNetworkType, selected: filters.networkType, apply: (key: string) => pick({ networkType: key }) },
            { id: "browser", title: "Browser", list: o.byBrowser, selected: filters.browser, apply: (key: string) => pick({ browser: key }) },
            { id: "os", title: "OS", list: o.byOs, selected: filters.os, apply: (key: string) => pick({ os: key }) },
            { id: "device", title: "Device", list: o.byDevice, selected: filters.device, apply: (key: string) => pick({ device: key }) },
            { id: "release", title: "Release", list: o.byRelease, selected: filters.release, apply: (key: string) => pick({ release: key }) },
          ]).filter((g) => g.list.length > 0).map((group) => {
            const peak = Math.max(1, ...group.list.map((b) => b.calls));
            const open = bdOpen[group.id] ?? false;
            const shown = open ? group.list : group.list.slice(0, BREAKDOWN_PREVIEW);
            const hidden = group.list.length - shown.length;
            return (
              <section key={group.id} className="card calls-bd" aria-label={`${group.title} breakdown`}>
                <header className="calls-bd-head">
                  <h2>{group.title}</h2>
                  <span className="calls-bd-n">{group.list.length}</span>
                  <InfoButton about={`the ${group.title} breakdown`} hint={hints.breakdown(group.title)} />
                </header>
                <table className="calls-bd-table">
                  <thead>
                    <tr>
                      <th>{group.title}</th>
                      <th className="calls-bd-share">Calls</th>
                      <th>Quality</th>
                      <th>Relayed</th>
                      <th>Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((b) => (
                      <tr key={b.key} className={group.selected === b.key ? "is-on" : undefined}>
                        <td>
                          <button type="button" className="calls-bd-name" aria-pressed={group.selected === b.key} onClick={() => group.apply(b.key)} title={`Show only ${b.key || "unknown"}`}>
                            {b.key || "unknown"}
                          </button>
                        </td>
                        <td className="calls-bd-share">
                          <span className="calls-bd-bar" aria-hidden="true"><i style={{ width: `${(b.calls / peak) * 100}%` }} /></span>
                          <span className="calls-bd-count">{b.calls.toLocaleString()}</span>
                        </td>
                        <td>{b.qualityP50 == null ? "—" : Math.round(b.qualityP50)}</td>
                        <td>{b.relayPct == null ? "—" : pctText(b.relayPct, 0)}</td>
                        <td className={(b.failPct ?? 0) >= 20 ? "bad" : undefined}>{b.failPct == null ? "—" : pctText(b.failPct, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(hidden > 0 || (open && group.list.length > BREAKDOWN_PREVIEW)) && (
                  <button type="button" className="btn ghost calls-bd-more" onClick={() => setBdOpen((s) => ({ ...s, [group.id]: !open }))}>
                    {hidden > 0 ? `Show ${hidden} more` : "Show less"}
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}

      {tab === "overview" && o != null && o.calls === 0 && (
        <p className="muted calls-overview-empty">No calls in this range. Try a longer range.</p>
      )}

      {tab === "calls" && <section className="card calls-list" aria-label="Calls list">
        {listHead}
        {q.isLoading ? (
          <Skeleton lines={5} />
        ) : q.isError && rows.length === 0 ? (
          <ErrorState error={q.error} retry={() => void q.refetch()} />
        ) : rows.length === 0 ? (
          <div className="empty calls-empty" data-testid="calls-empty">
            <EmptyCallsMark />
            <h3>{filtered ? "No calls match these filters" : "No calls in this range"}</h3>
            <p>{filtered ? "Loosen a filter or pick a longer range to see more calls." : "Calls show up here a few seconds after they start. Try a longer range."}</p>
            {filtered && (
              <button type="button" className="btn" onClick={() => { setQInput(""); clearFilters(); }}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="table calls-table" data-testid="calls-table">
            <thead>
              <tr>
                <th className="calls-col-started">Started</th>
                <th className="calls-col-quality">Quality</th>
                <th className="calls-col-detail">Setup</th>
                <th className="calls-col-num">Duration</th>
                <th className="calls-col-num">Loss</th>
                <th className="calls-col-detail">Freezes</th>
                <th className="calls-state">State</th>
                <th className="calls-user">User</th>
                <th className="calls-col-actions" aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                if (item.kind === "group" && item.group) {
                  return <GroupTableRow key={`group:${item.group.id}`} group={item.group} onOpen={() => openGroup(item.group!.id)} />;
                }
                const c = item.call;
                if (!c) return null;
                return (
                <tr
                  key={`call:${c.id}`}
                  data-testid={`call-row-${c.id}`}
                  onClick={() => openCall(c.id)}
                  onKeyDown={(e) => (e.key === "Enter" ? openCall(c.id) : undefined)}
                  tabIndex={0}
                  aria-label={`Open the call on ${c.pc}, ${formatRelative(c.startedAt)}`}
                >
                  <td>
                    <span className="calls-when">{formatRelative(c.startedAt)}</span>
                    <div className="muted small">{c.pc}</div>
                  </td>
                  <td>
                    <QualityMeter score={c.qualityScore} />
                  </td>
                  <td className="calls-col-detail num">{dur(c.setupMs)}</td>
                  <td className="num">{dur(c.durationMs)}</td>
                  <td className={`num${(c.lossPctMax ?? 0) >= 5 ? " bad" : ""}`}>{pctText(c.lossPctAvg)}</td>
                  <td className="calls-col-detail num">
                    {c.freezeCount ? (
                      <span title={`${dur(c.freezeMsTotal)} frozen in total`}>
                        {c.freezeCount} · {dur(c.freezeMsTotal)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="calls-state">
                    <span className="cell-badges">
                      <CallStateBadge call={c} />
                      <MediaErrorBadges errors={c.mediaErrors} />
                      {c.oneWayAudio && (
                        <span className="badge danger" title="Audio flowed in one direction only">
                          one-way audio
                        </span>
                      )}
                      {c.oneWayVideo && (
                        <span className="badge danger" title="Video flowed in one direction only">
                          one-way video
                        </span>
                      )}
                      {c.issueIds.length > 0 && <span className="badge warn">{c.issueIds.length} issue{c.issueIds.length === 1 ? "" : "s"}</span>}
                    </span>
                  </td>
                  <td className="calls-user small"><EndUser id={c.endUserId} name={c.endUserName} /></td>
                  <td className="calls-row-actions">
                    <CallActions call={c} prefix="call" onCopied={copied} />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Phones get the same calls as cards. The desktop columns cannot be
            read sideways, and the facts that decide whether a call is worth
            opening are the badges and the handful of numbers below. */}
        {rows.length > 0 && (
          <ul className="call-card-list" aria-label="Calls">
            {rows.map((item) => {
              if (item.kind === "group" && item.group) {
                return <GroupPhoneCard key={`group:${item.group.id}`} group={item.group} onOpen={() => openGroup(item.group!.id)} />;
              }
              const c = item.call;
              if (!c) return null;
              return (
              <li
                className="call-card"
                key={`call:${c.id}`}
                data-testid={`call-card-${c.id}`}
                onClick={() => openCall(c.id)}
                onKeyDown={(e) => (e.key === "Enter" ? openCall(c.id) : undefined)}
                tabIndex={0}
                role="link"
                aria-label={`Open the call on ${c.pc}, ${formatRelative(c.startedAt)}`}
              >
                <div className="call-card-head">
                  <span className="call-card-when">
                    <strong>{formatRelative(c.startedAt)}</strong>
                    <small className="muted">{c.pc}</small>
                  </span>
                  <CallActions call={c} prefix="call-card" onCopied={copied} />
                </div>
                <QualityMeter score={c.qualityScore} width={120} />
                <div className="call-card-badges">
                  <CallStateBadge call={c} />
                  <PathBadge relayed={c.relayed} />
                  <MediaErrorBadges errors={c.mediaErrors} />
                  {c.oneWayAudio && (
                    <span className="badge danger" title="Audio flowed in one direction only">
                      one-way audio
                    </span>
                  )}
                  {c.oneWayVideo && (
                    <span className="badge danger" title="Video flowed in one direction only">
                      one-way video
                    </span>
                  )}
                  {c.issueIds.length > 0 && <span className="badge warn">{c.issueIds.length} issue{c.issueIds.length === 1 ? "" : "s"}</span>}
                </div>
                <dl className="call-card-stats">
                  {([
                    ["Setup", dur(c.setupMs)],
                    ["Duration", dur(c.durationMs)],
                    ["Network", c.networkType ?? "—"],
                    ["Loss", pctText(c.lossPctAvg)],
                    ["Freezes", freezeText(c)],
                    ["User", c.endUserName ? `${c.endUserName} · ${c.endUserId}` : (c.endUserId ?? "anonymous")],
                  ] as const).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd className={label === "Loss" && (c.lossPctMax ?? 0) >= 5 ? "bad" : undefined}>{value}</dd>
                    </div>
                  ))}
                </dl>
              </li>
              );
            })}
          </ul>
        )}
        {q.isFetchNextPageError && <ErrorState error={q.error} retry={() => void q.fetchNextPage()} />}
        <AutoLoadMore hasMore={!!q.hasNextPage && !q.isFetchNextPageError} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more calls" />
      </section>}
    </>
  );
}
